import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { FileLock, Download, History, RefreshCcw, AlertCircle, ChevronDown, ChevronRight, Search, Clock, Upload, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Controlled Documents vault UI — grouped + latest-only by default.
 *
 * Behaviour added Jun 1, 2026 (v5.13):
 *  - Files are categorised into ~10 folders by filename pattern + extension.
 *  - Each folder is a collapsible card. Default: collapsed except the folder
 *    holding the most recently modified file.
 *  - Within each folder, only the LATEST version of each base-key is shown.
 *    A per-folder "Show older versions (N)" toggle reveals history.
 *  - A global "Show all versions" master toggle bypasses the per-folder
 *    de-dupe (useful for full audit).
 *  - Search box filters by filename substring (case-insensitive).
 *  - Bottom "Latest at a glance" rail: the 8 most recently modified files
 *    across the whole vault (latest version of each base-key only), as
 *    quick-access pills.
 *
 * Data-testids preserved + new ones added per the project rule.
 */

// ───────────────────────── Categorisation ──────────────────────────

const FOLDERS = [
  // IJ-059 (2026-07-21) — Incidents & Complaints folder groups the SEV-0 files
  // (original complaint · Addendum A · Addendum B) so they surface as a first-
  // class section in the admin dashboard instead of falling into "Other".
  { id: 'incidents',    label: 'Incidents & Complaints',      match: /SEV0_Complaint|_SEV0_|Emergent_SEV|Incident_Response|Complaint_Addendum/i },
  { id: 'product',      label: 'Product',                     match: /^Product_/i },
  { id: 'sessions',     label: 'Session Continuity (SPDs)',  match: /SessionContinuityProtocol|SPD_/i },
  { id: 'budget',       label: 'Campaign & Budget',           match: /Campaign_Budget|Load_Projection/i },
  { id: 'migration',    label: 'Migration & Infra',           match: /Migration_Work|Client_Side_Migration|LocalFirst_Architecture|InfraRecommendations/i },
  { id: 'security',     label: 'Security & Privacy',          match: /Security_BRD|Security_Pre_Launch|Security_OnePager|Data_Policy/i },
  { id: 'brds',         label: 'BRDs',                        match: /Master_BRD|Agent_BRD|Atlas_BRD|Voice_Conversational_AI_BRD|Voice_BRD|Knowledge_Controls_Harness_BRD|Knowledge_Graph_BRD|SI_Agentic_Product_BRD|SI_Agentic_Product_Harness_BRD|SI_AGENTIC_BRD|Product_Harness_BRD|_Harness_BRD/i },
  { id: 'execution',    label: 'Execution & Planning',        match: /Execution_Spec|Phase_\d+_Plan|Agent_Skill_Framework/i },
  { id: 'data',         label: 'Data, Schema & KPI',          match: /Data_Schema|Executable_Data_Build_Spec|Scenario_Library|KPI_Dashboard/i },
  { id: 'qa',           label: 'QA & Workflow',               match: /QA_Regression|QA_Workflow/i },
  { id: 'architecture', label: 'Architecture & Audit',        match: /Solution_Architecture|OpenQuestions|GapAnalysis|Audit|Assessment|CrossDocument_Changelog/i },
  { id: 'media',        label: 'Images & Snapshots',          match: /Trust_Snapshot|cc_slide|slide\d+_Picture|fb\d+/i },
];

const MEDIA_EXTS = new Set(['png', 'jpg', 'jpeg', 'webp', 'svg', 'gif', 'html', 'pptx']);

function categorise(filename) {
  for (const f of FOLDERS) {
    if (f.match.test(filename)) return f.id;
  }
  const ext = (filename.split('.').pop() || '').toLowerCase();
  if (MEDIA_EXTS.has(ext)) return 'media';
  return 'other';
}

// ───────────────────────── Version + base-key parsing ──────────────────

/**
 * Strip the version + state suffix to derive a "base key" so we can group
 * different versions of the same underlying doc. Examples:
 *   SearchInc_Campaign_Budget_Load_Projection_v2.1.docx
 *     → base: SearchInc_Campaign_Budget_Load_Projection
 *   SearchInc_SessionContinuityProtocol_v5.13_OPEN.docx
 *     → base: SearchInc_SessionContinuityProtocol
 *   SearchInc_Agent_BRD_v3_8.docx
 *     → base: SearchInc_Agent_BRD
 */
function baseKey(filename) {
  let name = filename.replace(/\.[a-z0-9]+$/i, '');
  name = name.replace(/_v\d+(?:[._]\d+)*(?:_(?:OPEN|CLOSE|CLOSED|Amendment|AdditionsSpec))?$/i, '');
  return name;
}

/**
 * Parse a comparable version tuple from the filename. Anything missing
 * sorts low. State preference: CLOSE > OPEN > none at the same version.
 *
 * Returns array like [5, 13, 1] where the trailing element encodes state
 * (0=none, 1=OPEN, 2=CLOSE/CLOSED). Compare lexicographically.
 */
function versionTuple(filename) {
  const m = filename.match(/_v(\d+(?:[._]\d+)*)(?:_(OPEN|CLOSE|CLOSED|Amendment|AdditionsSpec))?/i);
  if (!m) return [0];
  const parts = m[1].split(/[._]/).map(n => parseInt(n, 10) || 0);
  const stateMap = { OPEN: 1, CLOSE: 2, CLOSED: 2, AMENDMENT: 3, ADDITIONSSPEC: 1 };
  const state = m[2] ? (stateMap[m[2].toUpperCase()] || 0) : 0;
  return [...parts, state];
}

function compareVersions(a, b) {
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const av = a[i] ?? 0, bv = b[i] ?? 0;
    if (av !== bv) return bv - av; // descending
  }
  return 0;
}

/** Reduce a list of docs to one entry per base-key (the latest). */
function pickLatestPerBase(docs) {
  const byBase = new Map();
  for (const d of docs) {
    const key = baseKey(d.filename);
    const v = versionTuple(d.filename);
    const cur = byBase.get(key);
    if (!cur || compareVersions(v, cur._v) < 0) {
      byBase.set(key, { ...d, _v: v });
    }
  }
  return [...byBase.values()];
}

// ───────────────────────── Component ──────────────────────────

export default function ControlledDocs() {
  const [docs, setDocs] = useState([]);
  const [archivedDocs, setArchivedDocs] = useState([]);
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingFile, setDownloadingFile] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditEvents, setAuditEvents] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [showAllVersions, setShowAllVersions] = useState(false);
  const [openFolders, setOpenFolders] = useState(() => new Set());
  const [folderHistoryOpen, setFolderHistoryOpen] = useState(() => new Set());

  // IJ-272 (2026-08-12) — operator-only Secure Vault Upload.
  // is_operator resolves via /api/auth/verify. The upload panel renders
  // only when true. The server still enforces the real gate.
  const [isOperator, setIsOperator] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const fileInputRef = useRef(null);

  const getToken = () => localStorage.getItem('startech_token');

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/api/admin/docs`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setDocs(res.data.documents || []);
      setArchivedDocs(res.data.archived || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load vault');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAudit = async () => {
    setAuditLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/docs/audit?days=30`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setAuditEvents(res.data.events || []);
    } catch {
      setAuditEvents([]);
    } finally {
      setAuditLoading(false);
    }
  };

  const handleDownload = async (filename) => {
    setDownloadingFile(filename);
    try {
      const res = await axios.get(`${API}/api/admin/docs/download/${encodeURIComponent(filename)}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      alert(`Download failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setDownloadingFile(null);
    }
  };

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  // IJ-272 · resolve is_operator once on mount so the Upload card only
  // renders for the env-admin identity. The server-side gate is the real
  // control; this just hides the surface for non-operator admins.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/auth/verify`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (!cancelled) setIsOperator(!!res.data?.is_operator);
      } catch {
        if (!cancelled) setIsOperator(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleUpload = async (evt) => {
    const file = evt.target.files?.[0];
    if (!file) return;
    setUploadError(null);
    setUploadSuccess(null);
    setUploadBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/api/admin/docs/upload`, form, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'multipart/form-data',
        },
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });
      setUploadSuccess(`Uploaded ${res.data.filename} (${(res.data.size_bytes / 1024).toFixed(1)} KB).`);
      await fetchDocs();
    } catch (err) {
      setUploadError(err.response?.data?.detail || err.message || 'Upload failed');
    } finally {
      setUploadBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ───── Grouping memo ─────
  const { grouped, recent } = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const filtered = q ? docs.filter(d => d.filename.toLowerCase().includes(q)) : docs;

    const byFolder = {};
    for (const f of FOLDERS) byFolder[f.id] = [];
    byFolder.other = [];
    for (const d of filtered) {
      const fid = categorise(d.filename);
      (byFolder[fid] || byFolder.other).push(d);
    }

    const grouped = [...FOLDERS, { id: 'other', label: 'Other' }]
      .map(f => {
        const all = byFolder[f.id] || [];
        const latest = pickLatestPerBase(all);
        const latestSorted = [...latest].sort((a, b) => compareVersions(a._v, b._v));
        const latestKeys = new Set(latestSorted.map(x => x.filename));
        const older = all.filter(x => !latestKeys.has(x.filename));
        const olderSorted = [...older].sort((a, b) => compareVersions(versionTuple(a.filename), versionTuple(b.filename)));
        return { ...f, latest: latestSorted, older: olderSorted, count: all.length };
      })
      .filter(f => f.count > 0);

    // Recent rail: top 8 by modified date, but only one entry per base-key.
    const seen = new Set();
    const recent = [...filtered]
      .sort((a, b) => new Date(b.modified) - new Date(a.modified))
      .filter(d => {
        const k = baseKey(d.filename);
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      })
      .slice(0, 8);

    return { grouped, recent };
  }, [docs, searchQuery]);

  // Auto-open the folder containing the single most recent file once data lands.
  useEffect(() => {
    if (!docs.length || openFolders.size > 0) return;
    const newest = [...docs].sort((a, b) => new Date(b.modified) - new Date(a.modified))[0];
    if (newest) {
      const fid = categorise(newest.filename);
      setOpenFolders(new Set([fid]));
    }
  }, [docs, openFolders.size]);

  const toggleFolder = (id) => {
    setOpenFolders(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const toggleFolderHistory = (id) => {
    setFolderHistoryOpen(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const fmtSize = (n) => {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
  };
  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString('en-CA', {
        year: 'numeric', month: 'short', day: '2-digit',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };

  // ───── Row + folder renderers ─────
  const renderRow = (d, key) => (
    <tr key={key || d.filename}
        className="border-b border-slate-900 hover:bg-slate-800/30"
        data-testid={`controlled-doc-row-${d.filename}`}>
      <td className="py-2 px-2 text-slate-300 font-mono text-[11px] break-all">{d.filename}</td>
      <td className="py-2 px-2 text-slate-500 tabular-nums whitespace-nowrap">{fmtSize(d.size_bytes)}</td>
      <td className="py-2 px-2 text-slate-500 whitespace-nowrap">{fmtDate(d.modified)}</td>
      <td className="py-2 px-2 text-right">
        <button
          onClick={() => handleDownload(d.filename)}
          disabled={downloadingFile === d.filename}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 border border-amber-500/40 hover:border-amber-400 transition-colors disabled:opacity-50"
          data-testid={`controlled-doc-download-${d.filename}`}
        >
          <Download size={11} />
          {downloadingFile === d.filename ? 'Downloading…' : 'Download'}
        </button>
      </td>
    </tr>
  );

  return (
    <div className="mt-8 p-5 bg-slate-900/70 rounded-xl border border-amber-500/30" data-testid="controlled-docs-panel">
      {/* Header */}
      <div className="flex items-start justify-between mb-4 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <FileLock className="text-amber-400" size={18} />
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-200">
              Controlled Documents
            </h2>
            <p className="text-[10px] text-slate-500 mt-0.5">
              Admin-only · /app/memory/secure_docs · every download audit-logged · grouped by type · latest version shown by default
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAllVersions(v => !v)}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] border transition-colors ${
              showAllVersions
                ? 'text-amber-300 bg-amber-500/15 border-amber-500/50'
                : 'text-slate-400 hover:text-amber-300 border-slate-700 hover:border-amber-500'
            }`}
            data-testid="controlled-docs-toggle-allversions"
          >
            <History size={12} />
            {showAllVersions ? 'Showing all versions' : 'Latest only'}
          </button>
          <button
            onClick={() => { setAuditOpen(o => !o); if (!auditOpen) fetchAudit(); }}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] text-slate-400 hover:text-amber-400 border border-slate-700 hover:border-amber-500 transition-colors"
            data-testid="controlled-docs-audit-btn"
          >
            <History size={12} />
            Audit history
          </button>
          <button
            onClick={fetchDocs}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] text-slate-400 hover:text-cyan-400 border border-slate-700 hover:border-cyan-500 transition-colors"
            data-testid="controlled-docs-refresh"
          >
            <RefreshCcw size={12} />
            Refresh
          </button>
        </div>
      </div>

      {/* IJ-272 · Secure Vault Upload · operator-only */}
      {isOperator && (
        <div
          className="mb-4 p-3 rounded-lg bg-amber-500/5 border border-amber-500/30"
          data-testid="controlled-docs-upload-panel"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-2">
              <Upload size={14} className="text-amber-400 mt-0.5" />
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-300">
                  Upload to Vault
                </p>
                <p className="text-[10px] text-slate-400 mt-0.5">
                  Operator only · 50 MB cap · .docx .pdf .md .txt .png .jpg .jpeg .xlsx .pptx
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".docx,.pdf,.md,.txt,.png,.jpg,.jpeg,.xlsx,.pptx"
                onChange={handleUpload}
                disabled={uploadBusy}
                className="hidden"
                data-testid="controlled-docs-upload-input"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadBusy}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-[11px] bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/50 hover:border-amber-400 transition-colors disabled:opacity-50"
                data-testid="controlled-docs-upload-btn"
              >
                <Upload size={11} />
                {uploadBusy ? 'Uploading…' : 'Choose file'}
              </button>
            </div>
          </div>
          {uploadError && (
            <div
              className="mt-2 flex items-center gap-2 p-2 rounded-md bg-rose-500/10 border border-rose-500/40 text-[11px] text-rose-300"
              data-testid="controlled-docs-upload-error"
            >
              <AlertCircle size={12} />
              <span>{uploadError}</span>
            </div>
          )}
          {uploadSuccess && (
            <div
              className="mt-2 flex items-center gap-2 p-2 rounded-md bg-emerald-500/10 border border-emerald-500/40 text-[11px] text-emerald-300"
              data-testid="controlled-docs-upload-success"
            >
              <CheckCircle2 size={12} />
              <span>{uploadSuccess}</span>
            </div>
          )}
        </div>
      )}

      {/* Search */}
      <div className="relative mb-4">
        <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Filter by filename…"
          className="w-full pl-7 pr-3 py-1.5 bg-slate-900 border border-slate-700 focus:border-amber-500 rounded-md text-[11px] text-slate-200 placeholder-slate-600 outline-none transition-colors"
          data-testid="controlled-docs-search"
        />
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-rose-500/10 border border-rose-500/40 text-xs text-rose-300 mb-3" data-testid="controlled-docs-error">
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <p className="text-xs text-slate-500">Loading vault…</p>
      ) : grouped.length === 0 ? (
        <p className="text-xs text-slate-500">
          {searchQuery ? `No documents match "${searchQuery}".` : 'Vault is empty.'}
        </p>
      ) : (
        <div className="space-y-2" data-testid="controlled-docs-folders">
          {grouped.map(folder => {
            const isOpen = openFolders.has(folder.id);
            const showHistory = showAllVersions || folderHistoryOpen.has(folder.id);
            const visibleRows = showHistory ? [...folder.latest, ...folder.older] : folder.latest;
            return (
              <div key={folder.id}
                   className="bg-slate-950/40 border border-slate-800 rounded-lg overflow-hidden"
                   data-testid={`folder-${folder.id}`}>
                <button
                  onClick={() => toggleFolder(folder.id)}
                  className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-900/60 transition-colors"
                  data-testid={`folder-toggle-${folder.id}`}
                >
                  <div className="flex items-center gap-2">
                    {isOpen ? <ChevronDown size={14} className="text-amber-400" /> : <ChevronRight size={14} className="text-slate-500" />}
                    <span className="text-[12px] font-semibold text-slate-200">{folder.label}</span>
                    <span className="text-[10px] text-slate-500">
                      {folder.latest.length} latest{folder.older.length > 0 ? ` · ${folder.older.length} older` : ''}
                    </span>
                  </div>
                </button>

                {isOpen && (
                  <div className="px-3 pb-3 pt-1" data-testid={`folder-body-${folder.id}`}>
                    <table className="w-full text-xs" data-testid={`folder-table-${folder.id}`}>
                      <thead>
                        <tr className="text-left border-b border-slate-800">
                          <th className="py-2 px-2 font-semibold text-slate-500">File</th>
                          <th className="py-2 px-2 font-semibold text-slate-500">Size</th>
                          <th className="py-2 px-2 font-semibold text-slate-500">Modified</th>
                          <th className="py-2 px-2 font-semibold text-slate-500"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleRows.map(d => renderRow(d, `${folder.id}-${d.filename}`))}
                      </tbody>
                    </table>
                    {!showAllVersions && folder.older.length > 0 && (
                      <button
                        onClick={() => toggleFolderHistory(folder.id)}
                        className="mt-2 text-[10px] text-slate-500 hover:text-amber-300 underline underline-offset-2"
                        data-testid={`folder-history-toggle-${folder.id}`}
                      >
                        {folderHistoryOpen.has(folder.id)
                          ? `Hide ${folder.older.length} older version${folder.older.length > 1 ? 's' : ''}`
                          : `Show ${folder.older.length} older version${folder.older.length > 1 ? 's' : ''}`}
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Audit panel */}
      {auditOpen && (
        <div className="mt-5 pt-4 border-t border-slate-800" data-testid="controlled-docs-audit-panel">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 font-semibold">Last 30 days of downloads</p>
          {auditLoading ? (
            <p className="text-xs text-slate-500">Loading…</p>
          ) : auditEvents.length === 0 ? (
            <p className="text-xs text-slate-500">No downloads recorded in window.</p>
          ) : (
            <div className="space-y-1 max-h-60 overflow-y-auto">
              {auditEvents.map((e, i) => (
                <div key={i} className="text-[11px] text-slate-400 flex items-center gap-3" data-testid={`audit-event-${i}`}>
                  <span className="text-slate-600 tabular-nums">{fmtDate(e.at)}</span>
                  <span className="text-amber-400">{e.actor}</span>
                  <span className="text-slate-500">downloaded</span>
                  <span className="text-slate-300 font-mono">{e.filename}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Bottom "Latest at a glance" rail */}
      {!loading && recent.length > 0 && (
        <div className="mt-6 pt-4 border-t border-slate-800" data-testid="controlled-docs-recent-rail">
          <div className="flex items-center gap-2 mb-3">
            <Clock size={12} className="text-amber-400" />
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Latest at a glance</p>
            <span className="text-[10px] text-slate-600">— most recent {recent.length} of {docs.length}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {recent.map(d => (
              <button
                key={`recent-${d.filename}`}
                onClick={() => handleDownload(d.filename)}
                disabled={downloadingFile === d.filename}
                title={`${d.filename}\n${fmtDate(d.modified)} · ${fmtSize(d.size_bytes)}`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] bg-amber-500/10 hover:bg-amber-500/20 text-amber-200 border border-amber-500/30 hover:border-amber-400 transition-colors disabled:opacity-50 max-w-[18rem] truncate"
                data-testid={`recent-doc-${d.filename}`}
              >
                <Download size={10} />
                <span className="truncate">{d.filename.replace(/^SearchInc_/, '').replace(/\.[a-z0-9]+$/i, '')}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Archived vault (retired ancestors, collapsed by default) */}
      {!loading && archivedDocs.length > 0 && (
        <div className="mt-6 pt-4 border-t border-slate-800" data-testid="controlled-docs-archived">
          <button
            onClick={() => setArchivedOpen(o => !o)}
            className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-slate-950/40 border border-slate-800 hover:bg-slate-900/60 transition-colors"
            data-testid="controlled-docs-archived-toggle"
          >
            <div className="flex items-center gap-2">
              {archivedOpen ? <ChevronDown size={14} className="text-slate-500" /> : <ChevronRight size={14} className="text-slate-500" />}
              <span className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Archived</span>
              <span className="text-[10px] text-slate-500">— retired ancestors · {archivedDocs.length} files</span>
            </div>
            <span className="text-[10px] text-slate-600">read-only reference</span>
          </button>
          {archivedOpen && (
            <div className="mt-2 space-y-1" data-testid="controlled-docs-archived-list">
              {archivedDocs.map(a => (
                <div
                  key={`arch-${a.filename}`}
                  className="flex items-center justify-between px-3 py-1.5 rounded-md bg-slate-950/30 border border-slate-900/50 text-[11px]"
                  data-testid={`archived-row-${a.filename}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <FileLock size={11} className="text-slate-600 flex-shrink-0" />
                    <span className="text-slate-400 truncate font-mono">{a.filename}</span>
                    <span className="text-slate-600 text-[10px]">{fmtSize(a.size_bytes)}</span>
                  </div>
                  <button
                    onClick={async () => {
                      setDownloadingFile(a.filename);
                      try {
                        const res = await axios.get(
                          `${API}/api/admin/docs/archived/download/${encodeURIComponent(a.filename)}`,
                          { headers: { Authorization: `Bearer ${getToken()}` }, responseType: 'blob' }
                        );
                        const url = window.URL.createObjectURL(new Blob([res.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', a.filename);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                      } catch (e) {
                        setError(e.response?.data?.detail || e.message || 'Archived download failed');
                      } finally {
                        setDownloadingFile(null);
                      }
                    }}
                    disabled={downloadingFile === a.filename}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] bg-slate-800/50 hover:bg-slate-700/50 text-slate-400 hover:text-slate-200 border border-slate-800 transition-colors disabled:opacity-50"
                    data-testid={`archived-download-${a.filename}`}
                  >
                    <Download size={10} />
                    {downloadingFile === a.filename ? 'Downloading…' : 'Download'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
